import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calculator, TrendingUp, BookOpen, ShoppingCart,
  BarChart3, Users, LogOut, Menu, Moon, Sun, Store, KeyRound,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/kalkulator", label: "Kalkulator HPP", icon: Calculator, testid: "nav-kalkulator" },
  { to: "/bep", label: "Kalkulator BEP", icon: TrendingUp, testid: "nav-bep" },
  { to: "/resep", label: "Bahan & Resep", icon: BookOpen, testid: "nav-resep" },
  { to: "/kasir", label: "Kasir (POS)", icon: ShoppingCart, testid: "nav-kasir" },
  { to: "/laporan", label: "Laporan", icon: BarChart3, testid: "nav-laporan" },
];

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);
  return { dark, toggle: () => setDark((d) => !d) };
}

function NavLinks({ onNavigate, isAdmin }) {
  const items = isAdmin ? [...NAV, { to: "/admin", label: "Admin", icon: Users, testid: "nav-admin" }] : NAV;
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors min-h-[44px] ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-5 py-5">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
        <Store className="h-5 w-5" />
      </div>
      <div className="leading-tight">
        <p className="font-heading text-base font-bold">KalkuBisnis</p>
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">UMKM</p>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-border bg-card lg:flex">
        <Brand />
        <div className="flex-1 overflow-y-auto pb-4">
          <NavLinks isAdmin={isAdmin} />
        </div>
        <div className="border-t border-border p-3">
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive min-h-[44px]"
          >
            <LogOut className="h-[18px] w-[18px]" /> Keluar
          </button>
        </div>
      </aside>

      {/* Top bar */}
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card/80 px-4 backdrop-blur-md lg:pl-72 no-print">
        <div className="flex items-center gap-2">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex w-72 flex-col p-0">
              <Brand />
              <div className="flex-1 overflow-y-auto pb-2">
                <NavLinks isAdmin={isAdmin} onNavigate={() => setOpen(false)} />
              </div>
              <div className="mt-auto border-t border-border p-3 space-y-1">
                <button
                  data-testid="mobile-change-password"
                  onClick={() => { setOpen(false); navigate("/ganti-sandi"); }}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground min-h-[44px]"
                >
                  <KeyRound className="h-[18px] w-[18px]" /> Ganti Password
                </button>
                <button
                  data-testid="mobile-logout-button"
                  onClick={() => { setOpen(false); handleLogout(); }}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive min-h-[44px]"
                >
                  <LogOut className="h-[18px] w-[18px]" /> Keluar
                </button>
              </div>
            </SheetContent>
          </Sheet>
          <span className="font-heading text-sm font-semibold text-muted-foreground">
            {user?.business_name || "KalkuBisnis UMKM"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate("/ganti-sandi")} data-testid="change-password-nav" aria-label="Ganti kata sandi">
            <KeyRound className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Ganti tema">
            {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          <div className="flex items-center gap-2 rounded-full border border-border bg-secondary/60 py-1 pl-1 pr-3">
            <Avatar className="h-7 w-7">
              <AvatarFallback className="bg-primary text-xs text-primary-foreground">{initials}</AvatarFallback>
            </Avatar>
            <span className="hidden text-sm font-medium sm:inline" data-testid="current-user-name">{user?.name}</span>
          </div>
        </div>
      </header>

      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8 animate-fade-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
